import * as React from 'react';
import { Row } from 'react-bootstrap';
import { Field } from 'redux-form';

import { useFieldBehaviorFilterContext } from 'af-root/context/fieldBehaviorFilterContext';

import { OperandType } from '@acceligentllc/shared/enums/operand';
import type { OperationType } from '@acceligentllc/shared/enums/operation';
import { MinOperandCount } from '@acceligentllc/shared/enums/operation';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';

import { bemElement } from 'af-utils/bem.util';
import { useToggle } from 'af-utils/react.util';
import * as ReduxUtils from 'af-utils/reduxForms.util';

import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';

import PlainCheckbox from 'af-components/Controls/Checkbox';

import styles from './styles.module.scss';
import type { ReportBlockFieldFormModel, CalculatedFieldOptionFormModel, BlockDropdownSection, FieldBehaviorFilterFormModel } from '../formModel';
import { getOptionFieldFilter, getOptionLabel } from './helpers';
import type { FieldBehaviorFilterValueOption } from './types';

interface SharedProps {
	additionalFilter?: (option: ReportBlockFieldFormModel) => boolean;
	fieldName: string;
	fieldsByIdMap: { [fieldId: string]: ReportBlockFieldFormModel; };
	index: number;
	onChange: (index: number, value: CalculatedFieldOptionFormModel) => void;
	onFilterChange: (index: number, value: Pick<FieldBehaviorFilterFormModel, 'reportBlockFieldToFilterByVirtualId'>) => void;
	onFieldFilterValueChange: (index: number, value: Pick<FieldBehaviorFilterFormModel, 'value'>) => void;
	clearFieldValueFilter: (index: number) => void;
	onRemove: (index: number) => void;
	operandCount: number;
	operation: Nullable<OperationType>;
	selectedFieldsMap: { [key: string]: number; };
	type: CalculatedFieldOptionFormModel['type'];
	unit: Nullable<QuantityUnitType>;
	isFilterSelected: boolean;
	selectedFilter: Nullable<FieldBehaviorFilterFormModel>;
	selectedValueFieldId: Nullable<string>;
	options: BlockDropdownSection<CalculatedFieldOptionFormModel>[];
	showBehaviorFilterCheckbox: boolean;
}

type OwnProps = SharedProps;

type Props = OwnProps;

const _renderSectionHeader = (section: BlockDropdownSection<string>) => {
	const className = bemElement('report-block-form-field-modal', 'dropdown-option', {
		primary: section.isPrimary ?? false,
		secondary: !section.isPrimary,
	});
	return (
		<div className={className}>
			<div className="report-block-form-field-modal__dropdown-option__item">
				{section.name}
			</div>
		</div>
	);
};

const _renderFilterSectionHeader = (section: BlockDropdownSection<string>) => {
	const className = bemElement('report-block-form-field-modal', 'dropdown-option', {
		primary: section.isPrimary ?? false,
		secondary: !section.isPrimary,
	});
	return (
		<div className={className}>
			<div className="report-block-form-field-modal__dropdown-option__item">
				{section.name}
			</div>
		</div>
	);
};

const _renderItem = (fieldName: string, label: string, blockName: Nullable<string>) => {
	return (
		<div>
			{blockName && <div className={bemElement('report-block-form-field-modal__dropdown-option', 'item', ['block'])}>{blockName}</div>}
			<div className="report-block-form-field-modal__dropdown-option__item">{fieldName}</div>
			<div className={bemElement('report-block-form-field-modal__dropdown-option', 'item', ['meta'])}>
				{label}
			</div>
		</div>
	);
};

const _renderFilterItem = (fieldName: string, label: string, blockName: Nullable<string>) => {
	return (
		<div>
			{blockName && <div className={bemElement('report-block-form-field-modal__dropdown-option', 'item', ['block'])}>{blockName}</div>}
			<div className="report-block-form-field-modal__dropdown-option__item">{fieldName}</div>
			<div className={bemElement('report-block-form-field-modal__dropdown-option', 'item', ['meta'])}>
				{label}
			</div>
		</div>
	);
};

const _renderConstant = (fieldName: string) => {
	return (
		<Field
			component={Input}
			format={ReduxUtils.formatDecimalNumber}
			id={fieldName}
			isRequired={true}
			label="Constant"
			name={`${fieldName}.constant`}
			placeholder="Enter Constant Value"
			type="number"
		/>
	);
};

const CalculatedFieldOptionWithFilter: React.FC<Props> = (props: Props) => {
	const {
		operation,
		operandCount,
		fieldsByIdMap,
		unit,
		index,
		onRemove,
		onChange,
		fieldName,
		selectedFieldsMap,
		additionalFilter,
		type,
		onFilterChange,
		onFieldFilterValueChange,
		clearFieldValueFilter,
		isFilterSelected,
		selectedFilter,
		selectedValueFieldId,
		showBehaviorFilterCheckbox,
	} = props;

	const [
		selectableFields,
		setSelectableFields,
	] = React.useState<BlockDropdownSection<CalculatedFieldOptionFormModel>[]>([]);

	const filterContext = useFieldBehaviorFilterContext();

	const fieldBehaviorFilterValues = React.useMemo(() => {
		if (filterContext.useFieldBehaviorFilter && !!selectedFilter) {
			return filterContext.fieldBehaviorFilterValuesMap[selectedFilter.reportBlockFieldToFilterByVirtualId] ?? [];
		}
		return [];
	}, [filterContext, selectedFilter]);

	const fieldBehaviorFilters = React.useMemo(() => {
		if (filterContext.useFieldBehaviorFilter && !!selectedValueFieldId) {
			return filterContext.fieldBehaviorFilterOptions.filter(
				(_block) => _block.blockVirtualId === fieldsByIdMap[selectedValueFieldId]?.reportBlockVirtualId
			);
		}
		return [];
	}, [fieldsByIdMap, filterContext, selectedValueFieldId]);

	const {
		value: isBehaviorFilterSelectionChecked,
		toggle: toggleBehaviorFilterSelection,
	} = useToggle(!!selectedFilter);

	const handleToggleBehaviorFilterSelection = React.useCallback(() => {
		if (isBehaviorFilterSelectionChecked) {
			clearFieldValueFilter(index);
		}
		toggleBehaviorFilterSelection();
	}, [clearFieldValueFilter, index, isBehaviorFilterSelectionChecked, toggleBehaviorFilterSelection]);

	const getFilterFunc = React.useCallback(() => {
		return getOptionFieldFilter(operation, unit, selectedFieldsMap, fieldsByIdMap, additionalFilter);
	}, [operation, unit, selectedFieldsMap, fieldsByIdMap, additionalFilter]);

	React.useEffect(() => {
		let newFields: BlockDropdownSection<CalculatedFieldOptionFormModel>[] = [];
		const optionsFilter = getFilterFunc();
		newFields = props.options.reduce<BlockDropdownSection<CalculatedFieldOptionFormModel>[]>((_acc, _section) => {
			const filteredFields = (_section?.fields ?? []).filter((_field) => optionsFilter(_field, index));
			if (filteredFields.length) {
				_acc.push({
					..._section,
					fields: filteredFields,
				});
			}
			return _acc;
		}, []);
		setSelectableFields(newFields);
	}, [index, getFilterFunc, props.options]);

	const renderOptionItem = React.useCallback((option: CalculatedFieldOptionFormModel) => {
		const field = option.id ? fieldsByIdMap[option.id] : null;
		return field
			? _renderItem(field.name, getOptionLabel(field), null)
			: null;
	}, [fieldsByIdMap]);

	const renderFilterOptionItem = React.useCallback((option: { reportBlockFieldToFilterByVirtualId: string; }) => {
		const field = option.reportBlockFieldToFilterByVirtualId ? fieldsByIdMap[option.reportBlockFieldToFilterByVirtualId] : null;
		return field
			? _renderFilterItem(field.name, getOptionLabel(field), null)
			: null;
	}, [fieldsByIdMap]);

	const remove = React.useCallback(() => {
		onRemove(index);
	}, [index, onRemove]);

	const change = React.useCallback((value: CalculatedFieldOptionFormModel) => {
		onChange(index, { id: value.id, constant: null, type: OperandType.FIELD, index, isOperandBlockInPrimarySegment: value.isOperandBlockInPrimarySegment });
	}, [index, onChange]);

	const handleOnFilterChange = React.useCallback((value: Pick<FieldBehaviorFilterFormModel, 'reportBlockFieldToFilterByVirtualId'>) => {
		onFilterChange(index, value);
	}, [index, onFilterChange]);

	const handleOnFilterValueChange = React.useCallback((option: FieldBehaviorFilterValueOption) => {
		onFieldFilterValueChange(index, { value: { dropdownValue: option.dropdownValue } });
	}, [index, onFieldFilterValueChange]);

	const renderFieldDropdown = React.useCallback(() => {
		if (type === OperandType.CONSTANT) {
			return _renderConstant(fieldName);
		}
		return (
			<Field
				className="report-block-form-field-modal__calculation-dropdown"
				component={Dropdown}
				fixed={true}
				id={fieldName}
				label={`Field #${index + 1}`}
				name={fieldName}
				onValueChange={change}
				placeholder="Select"
				renderMenuItem={renderOptionItem}
				renderSectionHeader={_renderSectionHeader}
				sectionOptionsKey="fields"
				sections={selectableFields}
				sectionTitleKey="name"
				useSectionList
				valueKey="id"
				withCaret={true}
			/>
		);
	}, [change, fieldName, index, renderOptionItem, selectableFields, type]);

	const filterValueDrodownOptions = React.useMemo((): FieldBehaviorFilterValueOption[] => {
		return fieldBehaviorFilterValues.map((_v, _index) => ({ id: _index, dropdownValue: _v }));
	}, [fieldBehaviorFilterValues]);

	const renderFieldBehaviorFilterSelection = React.useCallback(() => {
		if (!isBehaviorFilterSelectionChecked) {
			return null;
		}
		return (
			<>
				<Row>
					<Field
						className="report-block-form-field-modal__calculation-dropdown"
						component={Dropdown}
						fixed={true}
						id={`${fieldName}.fieldBehaviorFilter`}
						label={'Filter with:'}
						name={`${fieldName}.fieldBehaviorFilter`}
						onValueChange={handleOnFilterChange}
						placeholder="Select"
						renderMenuItem={renderFilterOptionItem}
						renderSectionHeader={_renderFilterSectionHeader}
						sectionOptionsKey="fields"
						sections={fieldBehaviorFilters}
						sectionTitleKey="name"
						useSectionList
						valueKey="reportBlockFieldToFilterByVirtualId"
						withCaret={true}
					/>
					{isFilterSelected &&
						<Field
							alwaysShowErrors={true}
							className={styles.white}
							component={Dropdown}
							id={`${fieldName}.fieldBehaviorFilter.value`}
							label={'Filter Value'}
							labelKey={'dropdownValue'}
							name={`${fieldName}.fieldBehaviorFilter.value`}
							onValueChange={handleOnFilterValueChange}
							options={filterValueDrodownOptions}
							placeholder="Select"
							valueKey="id"
							withCaret={true}
						/>
					}
				</Row>
			</>);
	}, [
		fieldBehaviorFilters,
		fieldName,
		filterValueDrodownOptions,
		handleOnFilterChange,
		handleOnFilterValueChange,
		isBehaviorFilterSelectionChecked,
		isFilterSelected,
		renderFilterOptionItem,
	]);

	return (
		<div className={styles.container}>
			<Row className={styles.field}>
				{renderFieldDropdown()}
				{operation && operandCount > MinOperandCount[operation] &&
					<Button
						icon="delete"
						onClick={remove}
						style="link-danger"
					/>
				}
			</Row>
			{renderFieldBehaviorFilterSelection()}
			{showBehaviorFilterCheckbox &&
				<Row className={styles.filterCheckboxContainer}>
					<PlainCheckbox
						handleChange={handleToggleBehaviorFilterSelection}
						inline={true}
						isChecked={isBehaviorFilterSelectionChecked}
						isStandalone={true}
						label={'Field behavior filter'}
						tooltipMessage={'Enables choosing a filter when doing calculations'}
					/>
				</Row>
			}
		</div>
	);
};

export default React.memo(CalculatedFieldOptionWithFilter);
